import { message } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Form, Formik } from "formik";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useGetData from "../../api/useGetData";
import AppInput from "../../common/AppInput";
import SelectInput from "../../common/SelectInput";
import { registerUserInfoValidation } from "../../utils/validations";
import alphabeticalSort from "../../utils/alphabeticalSort";

dayjs.extend(customParseFormat);

const currentYear = dayjs().year();
const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
const months = Array.from({ length: 12 }, (_, i) => i + 1);
const days = Array.from({ length: 31 }, (_, i) => i + 1);

/**
 * @typedef {Object} UserInfoForm
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} gender
 * @property {string} nationality
 * @property {number} year
 * @property {number} month
 * @property {number} day
 */

/**
 * A component that represents the user info step form of the registeration process.
 * It is stored in a separate component because there are two different places where the registeration takes place with some differences in each.
 * It is intended for this component to not call the useContext hook directly, but for its parent to do so instead.
 *
 * @param {Object} props
 * @param {UserInfoForm} props.initialValues - The initial values of the form, the phone
 * @param {(info: import("./PasswordsStepForm").UserInfo)=>void} props.userInfoSubmit - A callback function that runs when the form is successfully submitted
 * @returns {JSX.Element}
 */

const UserInfoForm = ({ initialValues, userInfoSubmit }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation("global");

  const onSubmit = (values, { setFieldError }) => {
    console.log(values);
    if (
      !dayjs(
        `${values.year}-${values.month}-${values.day}`,
        "YYYY-M-D",
        true
      ).isValid()
    ) {
      message.error(t("errors.select_valid_date"));
      return;
    }
    userInfoSubmit({
      firstName: values.first_name,
      lastName: values.last_name,
      gender: values.gender,
      dob: `${values.year}-${values.month}-${values.day}`,
      nationality: values.nationality,
    });
  };

  const { data: genderData } = useGetData({
    route: "api/method/airport_transport.api.user.get_gender",
  });
  const {
    data: nationaliyData,
    getData: getNationalityData,
    loading: loadinNationality,
  } = useGetData({
    route: "api/method/airport_transport.api.user.get_nationality",
  });

  const nationalities = [...nationaliyData].sort(alphabeticalSort);

  useEffect(() => {
    if (!loadinNationality) {
      getNationalityData({ params: {} });
    }
  }, [language]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={registerUserInfoValidation}
      onSubmit={onSubmit}
    >
      {({ setFieldValue, errors, touched }) => (
        <Form className="mx-auto w-full">
          <div className="flex flex-col  md:flex-row justify-between items-center mt-4">
            <div className={`w-full md:w-1/2 mr-0 md:ms-1`}>
              <AppInput
                name="first_name"
                type="text"
                label={t("my_profile.first_name_text")}
                placeholder={t("my_profile.first_name_text")}
                required
                error={errors.first_name}
                touched={touched.first_name}
              />
            </div>
            <div className={`w-full md:w-1/2 mr-0 md:ms-1`}>
              <AppInput
                name="last_name"
                type="text"
                label={t("my_profile.last_name_text")}
                placeholder={t("my_profile.last_name_text")}
                required
                error={errors.last_name}
                touched={touched.last_name}
              />
            </div>
          </div>
          <div>
            <div className="flex flex-col gap-1">
              <SelectInput
                name="gender"
                onChange={(value) => setFieldValue("gender", value)}
                placeholder={t("select_gender")}
                label={t("my_profile.gender_text")}
                options={genderData.map((g) => ({
                  label: t(g),
                  value: g,
                }))}
                required
              />
            </div>
          </div>
          <div>
            <div className="my-4 flex flex-col gap-1">
              <SelectInput
                label={t("my_profile.nationality_text")}
                onChange={(value) => setFieldValue("nationality", value)}
                placeholder={t("select_nationality")}
                options={nationalities?.map((nationality) => ({
                  label: nationality,
                  value: nationality,
                }))}
                name="nationality"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="flex flex-col gap-1">
              <SelectInput
                label={t("my_profile.day_text")}
                onChange={(value) => setFieldValue("day", value)}
                placeholder={t("select_day")}
                name="day"
                options={days.map((day) => ({
                  label: day,
                  value: day,
                }))}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <SelectInput
                label={t("my_profile.month_text")}
                onChange={(value) => setFieldValue("month", value)}
                placeholder={t("select_month")}
                name="month"
                options={months.map((month) => ({
                  label: month,
                  value: month,
                }))}
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <SelectInput
                label={t("my_profile.year_text")}
                onChange={(value) => setFieldValue("year", value)}
                placeholder={t("select_year")}
                name="year"
                options={years.map((year) => ({
                  label: year,
                  value: year,
                }))}
                required
              />
            </div>
          </div>
          <AppInput
            name="email"
            type="email"
            label={t("my_profile.email_text")}
            disabled
            error={errors.email}
            touched={touched.email}
          />
          <AppInput
            name="phone"
            type="text"
            label={t("my_profile.phone_number_text")}
            disabled
            error={errors.phone}
            touched={touched.phone}
          />
          <button
            type="submit"
            className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2"
          >
            {t("next_text")}
          </button>
        </Form>
      )}
    </Formik>
  );
};

export default UserInfoForm;
